import {
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from '@material-ui/core';
import { Formik } from 'formik';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { getType } from 'typesafe-actions';
import FormBtns from '../../../../common/components/buttons/formBtns';
import PhoneNumberTextField from '../../../../common/components/fields/PhoneNumberTextField';
import {
  createGroupVisitActions,
  updateGroupVisitActions,
} from '../../groupVisits.actions';
import {
  GroupVisitDto,
  groupVisitInit,
  groupVisitValidationSchema,
} from '../../groupVisits.dtos';

interface IProps {
  groupVisit: GroupVisitDto;
  onClose: () => void;
  onSendReset: () => void;
}

const GroupVisitForm: React.FC<IProps> = ({
  groupVisit,
  onClose,
  onSendReset,
}) => {
  const dispatch = useDispatch();

  const [dto, setDto] = useState<GroupVisitDto>(groupVisitInit);

  useEffect(() => {
    setDto(groupVisit);
  }, [groupVisit]);

  return (
    <Formik
      initialValues={dto}
      validationSchema={groupVisitValidationSchema}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={(values, { setSubmitting, resetForm }) => {
        setTimeout(() => {
          setSubmitting(false);
          resetForm();
        }, 700);

        let allNew = values.id === '';

        dispatch({
          type: allNew
            ? getType(createGroupVisitActions.request)
            : getType(updateGroupVisitActions.request),
          payload: { ...values },
        });

        onClose();
      }}
      enableReinitialize={true}
    >
      {({
        values,
        errors,
        isSubmitting,
        touched,
        setFieldValue,
        handleBlur,
        handleChange,
        handleSubmit,
      }) => {
        return (
          <form onSubmit={handleSubmit} noValidate>
            <Card>
              <CardContent>
                <Grid item xs={12}>
                  <Typography variant='subtitle1'>
                    Group Name and Contact
                  </Typography>
                </Grid>
                <TextField
                  size='small'
                  margin='none'
                  value={values.groupName || ''}
                  label='Group Name'
                  name='groupName'
                  onChange={handleChange}
                  onBlur={(e) => {
                    handleBlur(e);
                  }}
                  error={Boolean(errors.groupName && touched.groupName)}
                  helperText={
                    Boolean(errors.groupName) &&
                    touched.groupName &&
                    errors.groupName
                  }
                  fullWidth
                  required
                  autoFocus
                />
                <TextField
                  size='small'
                  margin='none'
                  value={values.contact || ''}
                  label='Contact'
                  name='contact'
                  onChange={handleChange}
                  onBlur={(e) => {
                    handleBlur(e);
                  }}
                  error={Boolean(errors.contact && touched.contact)}
                  helperText={
                    Boolean(errors.contact) && touched.contact && errors.contact
                  }
                  fullWidth
                  required
                />
                <TextField
                  size='small'
                  margin='none'
                  value={values.email || ''}
                  label='Email'
                  name='email'
                  onChange={handleChange}
                  onBlur={(e) => {
                    handleBlur(e);
                  }}
                  error={Boolean(errors.email && touched.email)}
                  helperText={
                    Boolean(errors.email) && touched.email && errors.email
                  }
                  fullWidth
                  required
                />
                <PhoneNumberTextField
                  size='small'
                  margin='none'
                  value={values.phone || ''}
                  label='Phone'
                  name='phone'
                  onChange={handleChange}
                  onBlur={(e) => {
                    handleBlur(e);
                  }}
                  error={Boolean(errors.phone && touched.phone)}
                  helperText={
                    Boolean(errors.phone) && touched.phone && errors.phone
                  }
                  fullWidth
                  required
                />
                <TextField
                  size='small'
                  margin='none'
                  value={values.groupSize || ''}
                  label='Group Size'
                  name='groupSize'
                  onChange={handleChange}
                  onBlur={(e) => {
                    handleBlur(e);
                  }}
                  error={Boolean(errors.groupSize && touched.groupSize)}
                  helperText={
                    Boolean(errors.groupSize) &&
                    touched.groupSize &&
                    errors.groupSize
                  }
                  fullWidth
                  required
                  type='number'
                />
                <TextField
                  size='small'
                  margin='none'
                  value={values.reservationId || ''}
                  label='Reservation ID'
                  name='reservationId'
                  onChange={handleChange}
                  onBlur={(e) => {
                    handleBlur(e);
                  }}
                  error={Boolean(errors.reservationId && touched.reservationId)}
                  helperText={
                    Boolean(errors.reservationId) &&
                    touched.reservationId &&
                    errors.reservationId
                  }
                  fullWidth
                />
                <TextField
                  size='small'
                  margin='none'
                  value={values.leaderName || ''}
                  label='Teacher/Leader Name'
                  name='leaderName'
                  onChange={handleChange}
                  onBlur={(e) => {
                    handleBlur(e);
                  }}
                  error={Boolean(errors.leaderName && touched.leaderName)}
                  helperText={
                    Boolean(errors.leaderName) &&
                    touched.leaderName &&
                    errors.leaderName
                  }
                  fullWidth
                />
                <TextField
                  size='small'
                  margin='none'
                  value={values.url || ''}
                  label='URL'
                  name='url'
                  onChange={handleChange}
                  onBlur={(e) => {
                    handleBlur(e);
                  }}
                  error={Boolean(errors.url && touched.url)}
                  helperText={Boolean(errors.url) && touched.url && errors.url}
                  fullWidth
                  inputProps={{ readOnly: true }}
                />
                <TextField
                  size='small'
                  margin='none'
                  type='date'
                  value={
                    moment(values.expirationDate)
                      .add('hours', 8)
                      .format('yyyy-MM-DD') || new Date()
                  }
                  label='Expiration Date'
                  name='expirationDate'
                  onChange={handleChange}
                  onBlur={(e) => {
                    handleBlur(e);
                  }}
                  error={Boolean(
                    errors.expirationDate && touched.expirationDate
                  )}
                  helperText={
                    Boolean(errors.expirationDate) &&
                    touched.expirationDate &&
                    errors.expirationDate
                  }
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                <Grid container justify='flex-end'>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name='isActive'
                        color='primary'
                        onClick={() =>
                          setFieldValue('isActive', !values.isActive)
                        }
                        size='small'
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.isActive || true}
                        checked={values.isActive}
                      />
                    }
                    label='Active'
                  />
                </Grid>
                <Grid container justify='space-between' alignItems='center'>
                  <Grid item>
                    <Button
                      color='secondary'
                      variant='text'
                      type='button'
                      onClick={onSendReset}
                      disabled={values.id === '' || values.email === ''}
                    >
                      RESET PASSWORD
                    </Button>
                  </Grid>
                  <FormBtns
                    isSubmitting={isSubmitting}
                    onClose={onClose}
                    saveText='Save'
                  />
                </Grid>
              </CardContent>
            </Card>
          </form>
        );
      }}
    </Formik>
  );
};

export default GroupVisitForm;
